import { type ReactNode, useEffect, useMemo, useState, useSyncExternalStore } from "react";

import { type Theme, ThemeContext } from "./theme-context";

const STORAGE_KEY = "pokifolio-theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

/** The saved choice; storage can be blocked (private windows), so failing quietly means "system". */
function savedTheme(): Theme {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : "system";
  } catch {
    return "system";
  }
}

function saveTheme(theme: Theme): void {
  try {
    if (theme === "system") window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Not saved; the choice still applies until the page is closed.
  }
}

function subscribeToSystem(onChange: () => void): () => void {
  if (typeof window.matchMedia !== "function") return () => undefined;
  const query = window.matchMedia(DARK_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

const systemIsDark = () => typeof window.matchMedia === "function" && window.matchMedia(DARK_QUERY).matches;

/** Light, dark or the computer's setting; adds the "dark" class to <html> when dark. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(savedTheme);
  const dark = useSyncExternalStore(subscribeToSystem, systemIsDark, () => false);
  const resolved = theme === "system" ? (dark ? "dark" : "light") : theme;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", resolved === "dark");
    root.style.colorScheme = resolved;
  }, [resolved]);

  const value = useMemo(
    () => ({
      theme,
      resolved,
      setTheme: (next: Theme) => {
        saveTheme(next);
        setThemeState(next);
      },
    }),
    [theme, resolved],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
